import type { ICompartiment } from "@/interfaces/ICompartiment"
import type { IEvolution } from "@/interfaces/IEvolution"
import type { IGamme } from "@/interfaces/IGamme"
import ProduitService from "@/service/ProduitService"
import { useProfileStore } from "@/store/ProfileStore"
import { defineStore, storeToRefs } from "pinia"
import { computed, ref, watch } from "vue"

export const useProduitStore = defineStore(
  "produit",
  () => {
    const profileStore = useProfileStore()
    const { profile } = storeToRefs(profileStore)

    const gammeService = new ProduitService()
    const gammes = ref<IGamme[]>([])
    const loading = ref<boolean>(false)
    const lastFetchTime = ref<number>(0)
    const evolutionCache = ref<Map<string, IEvolution>>(new Map())
    const loadingEvolutions = ref<Set<string>>(new Set())

    // Cache TTL de 10 minutes pour les gammes
    const CACHE_TTL = 10 * 60 * 1000

    const isCacheValid = (): boolean => {
      return Date.now() - lastFetchTime.value < CACHE_TTL
    }

    // Computed pour les gammes filtrées selon le profil
    const filteredGammes = computed(() => {
      if (profile.value === 3) {
        return gammes.value
          .map((g) => ({
            ...g,
            Portefeuilles: g.Portefeuilles.filter(
              (p) => !(p.Contractuel && !p.Dedie),
            ),
          }))
          .filter((g) => g.Portefeuilles.length)
      }
      return gammes.value
    })

    async function getGamme(forceRefresh = false): Promise<void> {
      if (!forceRefresh && gammes.value.length > 0 && isCacheValid()) {
        return
      }

      if (loading.value) {
        return // Éviter les appels multiples simultanés
      }

      loading.value = true
      try {
        gammes.value = await gammeService.getGamme()
        lastFetchTime.value = Date.now()
      } catch (error) {
        console.error("Erreur lors de la récupération des gammes:", error)
        throw error
      } finally {
        loading.value = false
      }
    }

    function getCompartiment(libelle: string): ICompartiment | undefined {
      return filteredGammes.value
        .flatMap((g) => g.Portefeuilles)
        .flatMap((p) => p.Compartiments)
        .find((c) => c.Libelle === libelle)
    }

    function getCompartimentByCode(code: string): ICompartiment | undefined {
      return filteredGammes.value
        .flatMap((g) => g.Portefeuilles)
        .flatMap((p) => p.Compartiments)
        .find((c) => c.Code === code)
    }

    async function getCompartimentEvolution(
      codeCompartiment: string,
    ): Promise<IEvolution> {
      // Vérifier le cache d'évolution
      const cached = evolutionCache.value.get(codeCompartiment)
      if (cached) {
        return cached
      }

      // Éviter les appels duplicatas
      if (loadingEvolutions.value.has(codeCompartiment)) {
        // Attendre que l'appel en cours se termine
        return new Promise((resolve, reject) => {
          const interval = setInterval(() => {
            if (!loadingEvolutions.value.has(codeCompartiment)) {
              clearInterval(interval)
              const evolution = evolutionCache.value.get(codeCompartiment)
              if (evolution) {
                resolve(evolution)
              } else {
                reject(
                  new Error(`Évolution non trouvée pour ${codeCompartiment}`),
                )
              }
            }
          }, 50)
        })
      }

      loadingEvolutions.value.add(codeCompartiment)

      try {
        const evolution =
          await gammeService.getCompartimentEvolution(codeCompartiment)

        // Mettre à jour le cache
        evolutionCache.value.set(codeCompartiment, evolution)

        // Mettre à jour l'objet compartiment dans les gammes si possible
        const compartiment = getCompartimentByCode(codeCompartiment)
        if (compartiment) {
          compartiment.Evolution = evolution
        }

        return evolution
      } catch (error) {
        console.error(
          `Erreur lors de la récupération de l'évolution pour ${codeCompartiment}:`,
          error,
        )
        throw error
      } finally {
        loadingEvolutions.value.delete(codeCompartiment)
      }
    }

    function clearCache(): void {
      gammes.value = []
      lastFetchTime.value = 0
      evolutionCache.value.clear()
      loadingEvolutions.value.clear()
    }

    // Recharger les gammes quand le profil change
    watch(profile, () => {
      if (gammes.value.length > 0) {
        // Les gammes sont déjà chargées, pas besoin de refetch
        // Le computed filteredGammes se chargera du filtrage
      }
    })

    return {
      gammes: filteredGammes, // Exposer les gammes filtrées
      rawGammes: gammes, // Exposer les gammes brutes si nécessaire
      loading,
      getGamme,
      getCompartiment,
      getCompartimentByCode,
      getCompartimentEvolution,
      clearCache,
      isCacheValid: () => isCacheValid(),
    }
  },
  {
    persist: {
      storage: sessionStorage,
    },
  },
)
